import React, { useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "./ui/dialog";
import { toast } from "sonner";
import { Send, MessageSquare, CheckCircle2 } from "lucide-react";

export default function ContactBusinessDialog({ business }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ sender_name: "", sender_email: "", sender_phone: "", subject: `Inquiry about ${business?.name || ""}`, body: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await api.post(`/businesses/${business.id}/messages`, form);
      setDone(true);
      toast.success("Message sent — they'll be in touch soon.");
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setDone(false); setError("");
    setForm({ sender_name: "", sender_email: "", sender_phone: "", subject: `Inquiry about ${business?.name || ""}`, body: "" });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button className="w-full btn-brand" data-testid="contact-biz-trigger">
          <MessageSquare className="h-4 w-4 mr-1.5" /> Send a message
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg" data-testid="contact-biz-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Message {business?.name}</DialogTitle>
          <DialogDescription>Your message goes straight to the business owner. They&apos;ll reply directly to your email.</DialogDescription>
        </DialogHeader>
        {done ? (
          <div className="py-8 text-center">
            <CheckCircle2 className="h-10 w-10 mx-auto text-emerald-600" />
            <div className="mt-4 font-display text-xl" data-testid="contact-biz-success">Message sent.</div>
            <p className="text-sm text-zinc-500 mt-1">A copy was emailed to the owner.</p>
            <Button className="mt-6" variant="outline" onClick={() => setOpen(false)}>Close</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="cb-sender_name">Your name *</Label>
                <Input id="cb-sender_name" name="sender_name" required maxLength={120} value={form.sender_name} onChange={(e) => set("sender_name", e.target.value)} data-testid="contact-biz-name" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="cb-sender_email">Email *</Label>
                <Input id="cb-sender_email" name="sender_email" type="email" required value={form.sender_email} onChange={(e) => set("sender_email", e.target.value)} data-testid="contact-biz-email" />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="cb-sender_phone">Phone (optional)</Label>
              <Input id="cb-sender_phone" name="sender_phone" value={form.sender_phone} onChange={(e) => set("sender_phone", e.target.value)} data-testid="contact-biz-phone" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="cb-subject">Subject *</Label>
              <Input id="cb-subject" name="subject" required maxLength={160} value={form.subject} onChange={(e) => set("subject", e.target.value)} data-testid="contact-biz-subject" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="cb-body">Message *</Label>
              <Textarea id="cb-body" name="body" required rows={5} maxLength={4000} value={form.body} onChange={(e) => set("body", e.target.value)} placeholder="Tell them about your project, budget, and timeline…" data-testid="contact-biz-body" />
              <div className="text-[10px] text-zinc-400 font-mono-tag text-right">{form.body.length}/4000</div>
            </div>
            {error && <div className="text-sm text-red-600" data-testid="contact-biz-error">{error}</div>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy} className="btn-brand" data-testid="contact-biz-submit">
                <Send className="h-4 w-4 mr-1.5" />{busy ? "Sending…" : "Send message"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
